/**
 * caseToleranceUtils.ts — WCO-2364 per-case weight tolerance for remainder Waste Tracking.
 *
 * Mirrors the legacy VB6 deviation check. The band for one case comes from the material's
 * inventory record:
 *   upper = unit_weight × (1 + dev_percentage / 100)
 *   lower = unit_weight × (1 − dev_percentage / 100)
 * plus the existing "> 0" rule.
 *
 * Option B (decided): a remainder INTACT case validates against the symmetric band
 * [lower, upper]; a remainder NON-INTACT case (opened/partial, legitimately light)
 * validates against the upper bound only — no lower bound.
 *
 * dev_percentage = 0 is a configured ZERO tolerance → band collapses to
 * [unit_weight, unit_weight] (an intact case must equal the full-case weight exactly).
 * A missing dev_percentage or a non-positive unit_weight means there is no usable
 * tolerance config → only the "> 0" rule applies.
 */

export interface CaseBand {
  lower: number;
  upper: number;
}

interface ToleranceMaterial {
  unit_weight?: number | null;
  dev_percentage?: number | null;
}

/**
 * Compute the per-case band, or return null when there is no usable tolerance config
 * (dev_percentage missing/NaN, or unit_weight missing/<= 0). A null band means callers
 * should enforce only the "> 0" rule.
 */
export function computeCaseBand(material?: ToleranceMaterial | null): CaseBand | null {
  if (!material) return null;
  const unitWeight = Number(material.unit_weight);
  if (!Number.isFinite(unitWeight) || unitWeight <= 0) return null;
  if (material.dev_percentage === null || material.dev_percentage === undefined) return null;
  const dev = Number(material.dev_percentage);
  if (!Number.isFinite(dev)) return null;
  return {
    lower: unitWeight * (1 - dev / 100),
    upper: unitWeight * (1 + dev / 100),
  };
}

export type CaseWeightVerdict =
  | { ok: true }
  | { ok: false; code: 'zero' }
  | { ok: false; code: 'above'; lower: number; upper: number }
  | { ok: false; code: 'below'; lower: number; upper: number };

/**
 * Validate a single remainder case weight against the VB6 band (option B).
 * @param isIntact true = remainder intact (symmetric band); false = non-intact (upper only)
 */
export function validateRemainderCaseWeight(
  weight: number,
  material: ToleranceMaterial | null | undefined,
  isIntact: boolean,
): CaseWeightVerdict {
  if (!(weight > 0)) return { ok: false, code: 'zero' };
  const band = computeCaseBand(material);
  if (!band) return { ok: true }; // no tolerance config → only "> 0" enforced
  if (weight > band.upper) return { ok: false, code: 'above', lower: band.lower, upper: band.upper };
  if (isIntact && weight < band.lower) return { ok: false, code: 'below', lower: band.lower, upper: band.upper };
  return { ok: true };
}
